import { Injectable, inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { Partner, MenuItem } from '../models/models';

export interface SeoConfig {
  title: string;
  description: string;
  keywords?: string;
  url?: string;
  image?: string;
  type?: 'website' | 'restaurant' | 'business.business' | 'product' | 'article';
  schema?: object | object[];
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private document = inject(DOCUMENT);

  private readonly defaultTitle = 'Comercio en Algodonales y Tiendas Locales | Pinsapo Market';
  private readonly defaultDescription =
    'Guía y mercado digital de comercio en Algodonales (Cádiz). Descubre tiendas locales, restaurantes, comida a domicilio, productos frescos y reservas online en Pinsapo Market.';
  private readonly defaultKeywords =
    'comercio algodonales, comercio en algodonales, tiendas algodonales, tienda algodonales, comercios algodonales, comprar en algodonales, restaurantes algodonales, reservas algodonales, comida a domicilio algodonales, Pinsapo Market, Sierra de Cadiz';
  private readonly defaultImage = 'https://app.pinsapomarket.com/assets/icon2.png';
  private readonly baseUrl = 'https://app.pinsapomarket.com';

  /**
   * Configura las metaetiquetas principales (Title, Description, Keywords, OpenGraph, Twitter Cards, Canonical y JSON-LD).
   */
  setPageSeo(config: SeoConfig) {
    const fullTitle = config.title.includes('Pinsapo Market')
      ? config.title
      : `${config.title} | Pinsapo Market`;

    this.titleService.setTitle(fullTitle);

    // Meta tags estándar
    this.metaService.updateTag({ name: 'description', content: config.description });
    this.metaService.updateTag({
      name: 'keywords',
      content: config.keywords || this.defaultKeywords
    });

    const pageUrl = config.url ? (config.url.startsWith('http') ? config.url : `${this.baseUrl}${config.url}`) : this.baseUrl;
    const imageUrl = config.image || this.defaultImage;
    const ogType = config.type || 'website';

    // Canonical link
    this.setCanonicalUrl(pageUrl);

    // OpenGraph
    this.metaService.updateTag({ property: 'og:type', content: ogType });
    this.metaService.updateTag({ property: 'og:site_name', content: 'Pinsapo Market' });
    this.metaService.updateTag({ property: 'og:locale', content: 'es_ES' });
    this.metaService.updateTag({ property: 'og:title', content: fullTitle });
    this.metaService.updateTag({ property: 'og:description', content: config.description });
    this.metaService.updateTag({ property: 'og:url', content: pageUrl });
    this.metaService.updateTag({ property: 'og:image', content: imageUrl });

    // Twitter Card
    this.metaService.updateTag({ property: 'twitter:card', content: 'summary_large_image' });
    this.metaService.updateTag({ property: 'twitter:title', content: fullTitle });
    this.metaService.updateTag({ property: 'twitter:description', content: config.description });
    this.metaService.updateTag({ property: 'twitter:url', content: pageUrl });
    this.metaService.updateTag({ property: 'twitter:image', content: imageUrl });

    // Schema.org JSON-LD
    if (config.schema) {
      this.setJsonLd(config.schema);
    }
  }

  /**
   * SEO optimizado para la pantalla de inicio o por ciudad/categoría
   */
  setHomeSeo(city: string | null = 'Algodonales', categoryName?: string | null) {
    const targetCity = city || 'Algodonales';
    const citySlug = targetCity.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    let title = `Comercio en ${targetCity} y Tiendas Locales | Pinsapo Market`;
    let description = `Guía y mercado digital de comercio en ${targetCity} (Cádiz). Descubre tiendas locales, restaurantes, comida a domicilio, productos frescos y reservas online en Pinsapo Market.`;
    let keywords = `comercio ${targetCity}, comercio en ${targetCity}, tiendas ${targetCity}, tienda ${targetCity}, comercios ${targetCity}, comprar en ${targetCity}, restaurantes ${targetCity}, reservas ${targetCity}, comida a domicilio ${targetCity}, Pinsapo Market, Sierra de Cádiz`;

    if (categoryName) {
      title = `${categoryName} en ${targetCity} | Tiendas y Comercio Local - Pinsapo Market`;
      description = `Descubre los mejores comercios y tiendas de ${categoryName.toLowerCase()} en ${targetCity}. Haz tu pedido a domicilio, reserva cita o compra online en comercios locales.`;
      keywords = `${categoryName} ${targetCity}, ${categoryName.toLowerCase()} ${targetCity}, tiendas de ${categoryName.toLowerCase()} ${targetCity}, ${keywords}`;
    }

    const jsonLdSchema = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: `Pinsapo Market - Comercio en ${targetCity}`,
        url: `${this.baseUrl}/${citySlug}`,
        description: description,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${this.baseUrl}/search?q={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: `¿Qué comercios y tiendas hay en ${targetCity}?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `En ${targetCity} puedes encontrar una amplia variedad de comercio local: restaurantes, bares de tapas, panaderías, floristerías, fisioterapia, moda, artesanía y productos típicos de la Sierra de Cádiz a través de Pinsapo Market.`
            }
          },
          {
            '@type': 'Question',
            name: `¿Cómo comprar o pedir comida a domicilio en las tiendas de ${targetCity}?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `A través de Pinsapo Market (app.pinsapomarket.com), puedes explorar los comercios y restaurantes de ${targetCity}, seleccionar productos o platos y recibirlos cómodamente a domicilio o recogerlos en tienda.`
            }
          },
          {
            '@type': 'Question',
            name: `¿Cómo registrar mi tienda o restaurante de ${targetCity} en Pinsapo Market?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `Los comerciantes de ${targetCity} pueden unirse a Pinsapo Market accediendo a la sección de Información y Planes para digitalizar su catálogo, gestionar pedidos y reservas online.`
            }
          }
        ]
      }
    ];

    this.setPageSeo({
      title,
      description,
      keywords,
      url: `/${citySlug}`,
      schema: jsonLdSchema
    });
  }

  /**
   * SEO optimizado para la ficha de un comercio/restaurante/tienda
   */
  setPartnerSeo(partner: Partner) {
    const city = partner.city || 'Algodonales';
    const categoriesText = partner.categories && partner.categories.length > 0
      ? partner.categories.join(', ')
      : 'comercio local';

    let businessType: 'Restaurant' | 'Store' | 'LocalBusiness' = 'LocalBusiness';
    let typeLabel = 'Comercio';

    if (partner.componenttype === 'restaurant' || partner.componenttype === 'delivery') {
      businessType = 'Restaurant';
      typeLabel = 'Restaurante / Bar';
    } else if (partner.componenttype === 'shop') {
      businessType = 'Store';
      typeLabel = 'Tienda';
    } else if (partner.componenttype === 'reservation' || partner.componenttype === 'booking') {
      businessType = 'LocalBusiness';
      typeLabel = 'Servicio y Reservas';
    }

    const title = `${partner.name} en ${city} | Carta, Pedidos y Reservas - Pinsapo Market`;
    const description = `${typeLabel} ${partner.name} en ${city} (${categoriesText}). Pide online a domicilio, consulta su carta y reserva cita o mesa en Pinsapo Market.`;
    const url = `/${city.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}/${partner.nameid || partner.id}`;
    const image = partner.imageUrl || (partner.galleryImages && partner.galleryImages[0]) || this.defaultImage;

    const schema: any = {
      '@context': 'https://schema.org',
      '@type': businessType,
      name: partner.name,
      image: image,
      url: `${this.baseUrl}${url}`,
      telephone: partner.phone || undefined,
      address: {
        '@type': 'PostalAddress',
        streetAddress: partner.address || undefined,
        addressLocality: city,
        addressRegion: 'Cádiz',
        addressCountry: 'ES'
      }
    };

    if (partner.coordinates) {
      schema.geo = {
        '@type': 'GeoCoordinates',
        latitude: partner.coordinates.lat,
        longitude: partner.coordinates.lng
      };
    }

    if (partner.rating) {
      schema.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: partner.rating,
        reviewCount: partner.reviewCount || 1
      };
    }

    const sameAsList = [
      partner.googleMapsUrl,
      partner.instagramUrl,
      partner.facebookUrl
    ].filter(Boolean) as string[];

    if (sameAsList.length > 0) {
      schema.sameAs = sameAsList;
    }

    if (businessType === 'Restaurant') {
      schema.hasMenu = `${this.baseUrl}${url}`;
      schema.priceRange = '€€';
      if (partner.isReservablePartner) {
        schema.acceptsReservations = 'True';
      }
    }

    const keywords = `${partner.name}, ${partner.name} ${city}, carta ${partner.name}, menu ${partner.name}, pedir ${partner.name}, reservar ${partner.name}, horario ${partner.name}, ${typeLabel.toLowerCase()} ${city}, ${city}`;

    this.setPageSeo({
      title,
      description,
      keywords,
      url,
      image,
      type: businessType === 'Restaurant' ? 'restaurant' : 'business.business',
      schema
    });
  }

  /**
   * SEO optimizado para un producto específico
   */
  setProductSeo(product: MenuItem, partner?: Partner | null) {
    const partnerName = partner?.name || 'Comercio Local';
    const city = partner?.city || 'Algodonales';
    const title = `${product.name} en ${partnerName} (${city}) - Pinsapo Market`;
    const description = `${product.description ? product.description + ' - ' : ''}Disponible en ${partnerName} (${city}) por ${product.price?.toFixed(2)}€. Pide online en Pinsapo Market.`;
    const image = product.imageUrl || partner?.imageUrl || this.defaultImage;
    const url = partner?.nameid
      ? `/${city.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}/${partner.nameid}/${product.id}`
      : `/${city.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}/${product.id}`;

    const schema: any = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      image: image,
      description: product.description || product.name,
      offers: {
        '@type': 'Offer',
        price: product.price || 0,
        priceCurrency: 'EUR',
        availability: product.isAvailable !== false ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        seller: {
          '@type': 'LocalBusiness',
          name: partnerName,
          addressLocality: city
        }
      }
    };

    this.setPageSeo({
      title,
      description,
      url,
      image,
      type: 'product',
      schema
    });
  }

  /**
   * Restablece los metadatos predeterminados
   */
  resetDefaultSeo() {
    this.setPageSeo({
      title: this.defaultTitle,
      description: this.defaultDescription,
      keywords: this.defaultKeywords,
      url: '/'
    });
  }

  /**
   * Inyecta o actualiza la etiqueta canónica en el <head>
   */
  setCanonicalUrl(url: string) {
    let link: HTMLLinkElement | null = this.document.querySelector("link[rel='canonical']");
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  /**
   * Inyecta o actualiza el bloque de datos estructurados Schema.org JSON-LD
   */
  setJsonLd(schema: object | object[]) {
    let script: HTMLScriptElement | null = this.document.querySelector("script#app-json-ld");
    if (!script) {
      script = this.document.createElement('script');
      script.id = 'app-json-ld';
      script.type = 'application/ld+json';
      this.document.head.appendChild(script);
    }
    script.text = JSON.stringify(schema, null, 2);
  }
}
